import { ICasinoFavoriteResponse } from "@finbackoffice/clientbff-client";
import {
    FC,
    PropsWithChildren,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { ClientBFFContext } from "./client.provider";

export type IFavoriteGamesState = {
    games: ICasinoFavoriteResponse[];
    loading: boolean;
};

export type ICasinoFavoritesContext = {
    favoriteGamesState: IFavoriteGamesState;
    updateFavoriteGames: (games: ICasinoFavoriteResponse[]) => void;
    isFavorite: (gameId: string) => boolean;
    handleFavoriteClick: (gameId: string) => Promise<void>;
};

export type ICasinoFavoritesProviderProps = {
    favoriteGames: ICasinoFavoriteResponse[] | null;
    mobile?: boolean;
};

export const CasinoFavoritesContext = createContext<ICasinoFavoritesContext>(null as any);

export type ICasinoFavoritesActionsContext = {
    handleFavoriteClick: (gameId: string) => Promise<void>;
};

export const CasinoFavoritesActionsContext = createContext<ICasinoFavoritesActionsContext>(
    null as any,
);

export const CasinoFavoritesProvider: FC<ICasinoFavoritesProviderProps & PropsWithChildren> = ({
    children,
    favoriteGames,
}) => {
    const client = useContext(ClientBFFContext);
    const [favoriteGamesState, setFavoriteGamesState] = useState<IFavoriteGamesState>({
        games: favoriteGames ?? [],
        loading: true,
    });

    useEffect(() => {
        if (!favoriteGames) {
            return;
        }

        setFavoriteGamesState({
            loading: false,
            games: favoriteGames,
        });
    }, [favoriteGames]);

    const favoriteIdsSet = useMemo(
        () => new Set(favoriteGamesState.games.map((g) => g.game_id)),
        [favoriteGamesState.games],
    );

    const isFavorite = useCallback(
        (gameId: string) => favoriteIdsSet.has(gameId),
        [favoriteIdsSet],
    );
    const isFavoriteRef = useRef(isFavorite);
    isFavoriteRef.current = isFavorite;
    const loadingRef = useRef(favoriteGamesState.loading);
    loadingRef.current = favoriteGamesState.loading;

    const updateFavoriteGames = useCallback((games: ICasinoFavoriteResponse[]) => {
        setFavoriteGamesState((state) => ({
            ...state,
            games,
        }));
    }, []);

    const createCasinoFavorite = useCallback(
        async (gameId: string) => {
            const response = await client.createCasinoFavorite({
                game_id: gameId,
            });

            if (response?.game_id === gameId) {
                setFavoriteGamesState((prev) => ({
                    ...prev,
                    games: [response, ...prev.games],
                }));
            }
        },
        [client],
    );

    const deleteCasinoFavorite = useCallback(
        async (gameId: string) => {
            await client.deleteCasinoFavorite(gameId);

            setFavoriteGamesState((prev) => ({
                ...prev,
                games: prev.games.filter((item) => item.game_id !== gameId),
            }));
        },
        [client],
    );

    const handleFavoriteClick = useCallback(
        async (gameId: string) => {
            if (!loadingRef.current) {
                setFavoriteGamesState((state) => ({
                    ...state,
                    loading: true,
                }));
                if (isFavoriteRef.current(gameId)) {
                    await deleteCasinoFavorite(gameId);
                } else {
                    await createCasinoFavorite(gameId);
                }
                setFavoriteGamesState((state) => ({
                    ...state,
                    loading: false,
                }));
            }
        },
        [createCasinoFavorite, deleteCasinoFavorite],
    );

    const value = useMemo(
        () => ({
            favoriteGamesState,
            updateFavoriteGames,
            isFavorite,
            handleFavoriteClick,
        }),
        [favoriteGamesState, updateFavoriteGames, isFavorite, handleFavoriteClick],
    );

    const actionsValue = useMemo(() => ({ handleFavoriteClick }), [handleFavoriteClick]);

    return (
        <CasinoFavoritesActionsContext.Provider value={actionsValue}>
            <CasinoFavoritesContext.Provider value={value}>
                {children}
            </CasinoFavoritesContext.Provider>
        </CasinoFavoritesActionsContext.Provider>
    );
};
